import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  StyleSheet, View, FlatList, Animated, useWindowDimensions, Platform, ScrollView, Modal
} from "react-native";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useAccountContext } from "../../../hooks/useAccountContext";
import { useLeave } from "../../../hooks/useLeave";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import { ActivityIndicator, Card } from "react-native-paper";
import CustomInput from "../../../components/CustomInput";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { useTranslation } from "react-i18next";
import { useTableHelper } from "../../../hooks/useTableHelper";
import PersonalLeaveBalancesScreen from "../../SelfService/MyLeaves/PersonalLeaveBalancesScreen";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import {
  changeDateFromDDMMYYYYtoYYYYMMDD,
  changeDateFromYYYYMMDDToDDMMYYYY,
} from "../../../constants/FormatDate";
import { useHRAdminEmployees } from "../../../hooks/useHRAdminEmployees";
import { showMessage } from "react-native-flash-message";

const NumberInput = ({
  index,
  item,
  itemKey,
  handleEditPress,
  editable = true,
}) => {
  const [number, setNumber] = useState(item[itemKey]);
  useEffect(() => {
    setNumber(item[itemKey]); // Update state when item changes
  }, [item[itemKey]]);
  return (
    <CustomInput
      value={number}
      setValue={(e) => {
        if (!editable) return;
        let onlyNum = e.replace(/[^0-9.]/g, ""); // allows numbers and dots
        let dotIndex = onlyNum.indexOf(".");
        if (dotIndex !== -1)
          onlyNum =
            onlyNum.slice(0, dotIndex + 1) +
            onlyNum.slice(dotIndex + 1).replace(/\./g, "");
        setNumber(onlyNum);
        handleEditPress(index, item, itemKey, onlyNum);
      }}
      maxLen={50}
      type="MAININPUT"
      keyboardType="numeric"
      editable={editable}
    />
  );
};

const LeaveAnalysisView = React.memo(
  ({
    item,
    index,
    handleEditPress,
    // handleDeleteAbsence,
    styles,
    resetDates,
    handlePress,
    leaveTypesOptions,
    onTypeChange,
    selectedCompany,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedData, setSelectedData] = useState({
      startDate: item.startDate || "",
      endDate: item.endDate || "",
    });
    const [rangeText, setRangeText] = useState("");
    const [localComments, setLocalComments] = useState(item.comments || "");
    const [userComments, setUserComments] = useState(item.userComments || "");
    const { t } = useTranslation();
    const [type, setType] = useState(null);
    const typeRef = useRef(type);

    //#region UseEffect
    useEffect(() => {
      typeRef.current = type;
    }, [type]);

    useEffect(() => {
      if (item.aCode !== undefined && item.aCode !== null) {
        setType(item.aCode);
      }
    }, [item.aCode]);

    const onTypeSelect = (value) => {
      setType(value);
      typeRef.current = value;
      onTypeChange(index, item, "aCode", value);
    };

    useEffect(() => {
      setLocalComments(item.comments || "");
    }, [item.comments]);

    useEffect(() => {
      setUserComments(item.userComments || "");
    }, [item.userComments]);

    useEffect(() => {
      if (rangeText === "" && item.startDate && item.endDate) {
        const from = changeDateFromYYYYMMDDToDDMMYYYY(item.startDate);
        const to = changeDateFromYYYYMMDDToDDMMYYYY(item.endDate);
        setRangeText(`${from} - ${to}`);
      }
    }, [item.startDate, item.endDate, rangeText]);

    const dateHelper = useCallback(() => {
      let dateArr = [];
      let tmpTdate = "";
      let tmpFdate = "";
      if (
        selectedData?.startDate &&
        selectedData?.startDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        tmpFdate = changeDateFromYYYYMMDDToDDMMYYYY(selectedData?.startDate);
      }
      if (
        selectedData?.endDate &&
        selectedData?.endDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        tmpTdate = changeDateFromYYYYMMDDToDDMMYYYY(selectedData?.endDate);
      }
      dateArr.push(tmpFdate);
      dateArr.push(tmpTdate);
      return dateArr;
    }, [selectedData]);

    const onShowCalendarPress = () => {
      setShowCalendar((prevState) => !prevState);
    };

    const handleDateChange = (newDates) => {
      const today = new Date().toISOString().split("T")[0];
      setSelectedData(newDates);
      const safeStartDate =
        newDates.startDate?.trim() === "" || !newDates.startDate
          ? today
          : newDates.startDate;
      const safeEndDate =
        newDates.endDate?.trim() === "" ? null : newDates.endDate;
      item.startDate = safeStartDate;
      item.endDate = safeEndDate;
      const dateArr = dateHelper();
      setRangeText(`${dateArr[0]} - ${dateArr[1]}`);
    };

    const toggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    useEffect(() => {
      if (resetDates) {
        const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
        setSelectedData({
          startDate: item.startDate || today,
          endDate: item.endDate || "",
        });
        const from = changeDateFromYYYYMMDDToDDMMYYYY(item.startDate || today);
        const to = changeDateFromYYYYMMDDToDDMMYYYY(item.endDate || "");
        setRangeText(`${from} - ${to}`);
      }
    }, [resetDates, item.startDate, item.endDate]);
    //#endregion

    const extractDateOnly = (dateTimeString) => {
      if (!dateTimeString) return "";
      // If dateTimeString is a Date object, convert to ISO string first
      if (dateTimeString instanceof Date) {
        return dateTimeString.toISOString().split("T")[0];
      }
      // Otherwise, assume string and split at 'T'
      return dateTimeString.split("T")[0];
    };
    return (
      <View>
        <View>
          <View style={styles.row}>
            <CustomInput
              value={
                selectedData.startDate
                  ? changeDateFromYYYYMMDDToDDMMYYYY(
                    extractDateOnly(selectedData.startDate)
                  )
                  : ""
              }
              setValue={() => { }}
              maxLen={50}
              editable={false}
              type="MAININPUT"
            />
            <CustomInput
              value={
                selectedData.endDate
                  ? changeDateFromYYYYMMDDToDDMMYYYY(
                    extractDateOnly(selectedData.endDate)
                  )
                  : ""
              }
              setValue={() => { }}
              type="MAININPUT"
              maxLen={50}
              editable={false}
            />
            <CustomDropDown
              defValue={item.aCode}
              options={leaveTypesOptions}
              setValue={onTypeSelect}
              type="FULL"
              disabled={true}
            />
            <NumberInput
              index={index}
              item={item}
              itemKey="duration"
              handleEditPress={handleEditPress}
              editable={false}
            />
            <View>
              {/* <CustomButton
                iconName="trash-outline"
                onPress={() => handleDeleteAbsence(item, index)}
                show="HOME_BELL"
                type="HOME_BELL"
                textTop="2"
                sz={30}
              /> */}
              <Ionicons
                name={
                  isExpanded
                    ? "chevron-down-outline"
                    : "chevron-forward-outline"
                }
                style={styles.icons}
                onPress={toggleExpand}
              />
            </View>
          </View>
          {isExpanded && (
            <>
              <View style={styles.row2}>
                <View style={[styles.item, { justifyContent: "flex-start" }]}>
                  <CustomLabel value="User Comments" type="SUBTITLE" />
                  <CustomInput
                    index={index}
                    value={userComments}
                    setValue={(text) => {
                      setUserComments(text);
                      handleEditPress(index, item, "userComments", text);
                    }}
                    type="MAININPUT"
                    editable={false}
                  />
                </View>
                <View style={[styles.item, { justifyContent: "flex-start" }]}>
                  <CustomLabel value="Comments" type="SUBTITLE" />
                  <CustomInput
                    index={index}
                    value={localComments}
                    setValue={(text) => {
                      setLocalComments(text);
                      handleEditPress(index, item, "comments", text);
                    }}
                    type="MAININPUT"
                    editable={false}
                  />
                </View>
                <View style={[styles.item, { justifyContent: "flex-start" }]}>
                  <CustomLabel value="Company" type="SUBTITLE" />
                  <CustomInput
                    index={index}
                    value={selectedCompany?.name || selectedCompany?.code || ""}
                    setValue={() => { }}
                    type="MAININPUT"
                    editable={false}
                  />
                </View>
              </View>
            </>
          )}
        </View>
        {showCalendar && (
          <View style={styles.secondLine}>
            <View
              style={{
                height:
                  Platform.OS === "web"
                    ? 285
                    : height < 670
                      ? (height * 40) / 100
                      : (height * 30) / 100,
                width: Platform.OS === "web" ? 485 : (width * 90) / 100,
                justifyContent: "center",
                alignContent: "center",
                borderRadius: 10,
                borderWidth: 0,
                borderColor: "#e8e8e8",
              }}
            >
              <CustomCalendar
                calendarType={"RangePicker"}
                setRange={handleDateChange}
                range={selectedData}
              />
            </View>
          </View>
        )}
      </View>
    );
  }
);

const HRAdminEmplLeaveAnalysisScreen = (props) => {
  const { selectedEmployee } = props;
  const { selectedCompany, employeeID } = props;
  const { user, clientApi } = useAuthContext();
  const { dispatch } = useAccountContext();
  const { GetFiscalYears } = useLeave();
  const [employeeFiscalYears, setEmployeeFiscalYears] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState("");
  const [showAbsenceAnalysis, setShowAbsenceAnalysis] = useState(false);
  const [leaveTypesOptions, setLeaveTypesOptions] = useState([]);
  const { width, height } = useWindowDimensions();
  const { colors } = useTheme();
  const common = createCommonStyles(colors);
  const styles = makeStyles(colors, width, props);
  const [isLoading, setIsLoading] = useState(true);
  const { GetLeaveTypes } = useLeave();
  const [noFiscalYearsFound, setNoFiscalYearsFound] = useState(false);
  const [fiscalYearsLoaded, setFiscalYearsLoaded] = useState(false);
  const [leaveAnalysisData, setLeaveAnalysisData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [absenceData, setAbsenceData] = useState([]);
  const [changes, setChanges] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  // const [deletedItems, setDeletedItems] = useState([]);
  // const [insertData, setInsertData] = useState([]);
  const [resetDates, setResetDates] = useState([false]);
  const {
    // SaveAbsenceAnalysis,
    // DeleteAbsenceAnalysis,
    GetAbsenceAnalysis,
    leaveAnalysisGetById,
    createLeaveAnalysis,
    reconcileLeaveBalance
  } = useHRAdminEmployees();
  const { rowEdit, rowDelete, rowAdd, handleCancelChanges, hasDuplicates } =
    useTableHelper();
  const ref = useRef(null);
  const { t } = useTranslation();

  const pendingBalanceChanges = useRef({});
  const [balancesVersion, setBalancesVersion] = useState(0);
  const [balancesResetVersion, setBalancesResetVersion] = useState(0);

  // Create custom year leave analysis state variable
  const [showCreateYearModal, setShowCreateYearModal] = useState(false);
  const [customYear, setCustomYear] = useState("");
  const [customYearError, setCustomYearError] = useState("");

  useEffect(() => {
    const fetchLeaveAnalysis = async () => {
      if (
        fiscalYearsLoaded &&
        selectedFiscalYear &&
        props.selectedEmployee?.employeeID
      ) {
        setIsLoading(true);
        try {
          const data = await leaveAnalysisGetById(
            props.selectedEmployee.employeeID,
            selectedFiscalYear
          );
          const leaveAnalysisData = data || [];
          setLeaveAnalysisData(leaveAnalysisData);
          setOriginalData(
            typeof structuredClone === "function"
              ? structuredClone(leaveAnalysisData)
              : JSON.parse(JSON.stringify(leaveAnalysisData))
          );
        } catch (error) {
          console.error("Error fetching leave analysis:", error);
          setLeaveAnalysisData([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setLeaveAnalysisData([]);
      }
    };
    fetchLeaveAnalysis();
  }, [selectedFiscalYear, fiscalYearsLoaded, props.selectedEmployee]);

  useEffect(() => {
    const fetchLeaveTypes = async () => {
      setIsLoading(true);
      if (props.selectedEmployee?.employeeID && props.selectedCompany?.code) {
        const types = await GetLeaveTypes(
          props.selectedEmployee.employeeID,
          props.selectedCompany.code
        );

        if (types && Array.isArray(types)) {
          // Map to dropdown options format if needed
          const options = types.map((type) => ({
            label: type.description,
            value: type.aCode,
          }));
          setLeaveTypesOptions(options);
        } else {
          setLeaveTypesOptions([]);
        }
      } else {
        setLeaveTypesOptions([]);
      }
      setIsLoading(false)
    };

    fetchLeaveTypes();
  }, [props.selectedEmployee, props.selectedCompany]);

  //MUST CHANGE TO DYNAMICALLY FETCH BY COMPANY
  const getFiscalYearDateRange = (fiscalYear) => {
    // fiscalYear is a string or number like "2026"
    const year = parseInt(fiscalYear, 10);
    const startDate = `${year - 1}-01-01`;
    const endDate = `${year}-12-31`;
    return { startDate, endDate };
  };

  useEffect(() => {
    const fetchFiscalYears = async () => {
      setFiscalYearsLoaded(false);
      setNoFiscalYearsFound(false);
      try {
        const yrs = await GetFiscalYears(props.selectedEmployee?.employeeID);
        setFiscalYears(yrs);
        if (Array.isArray(yrs) && yrs.length) {
          const mostRecent = [...yrs].sort(
            (a, b) => parseInt(b.value, 10) - parseInt(a.value, 10)
          )[0].value;
          setSelectedFiscalYear(mostRecent);
        } else {
          setSelectedFiscalYear(null);
          setNoFiscalYearsFound(true);
        }
      } catch (error) {
        console.error("Error fetching fiscal years:", error);
        showMessage({ message: "An error occurred while fetching fiscal years.", type: "danger", duration: 3000 });
        setFiscalYears([]);
        setSelectedFiscalYear(null);
      } finally {
        setFiscalYearsLoaded(true);
      }
    };
    if (props.selectedEmployee?.employeeID) fetchFiscalYears();
    else {
      setFiscalYears([]);
      setSelectedFiscalYear(null);
      setFiscalYearsLoaded(false);
    }
  }, [props.selectedEmployee?.employeeID]);

  const onFiscalYearChange = (year) => {
    const fy =
      typeof year === "object"
        ? String(year?.value ?? year?.label ?? "")
        : String(year ?? "");
    setSelectedFiscalYear(fy);
  };

  useEffect(() => {
    if (
      fiscalYearsLoaded &&
      selectedFiscalYear &&
      props.selectedEmployee?.employeeID
    ) {
      const fetchData = async () => {
        setIsLoading(true);
        const { startDate, endDate } =
          getFiscalYearDateRange(selectedFiscalYear);
        try {
          const data = await GetAbsenceAnalysis(
            props.selectedEmployee.employeeID,
            startDate,
            endDate
          );
          setAbsenceData(data);
          setOriginalData(
            typeof structuredClone === "function"
              ? structuredClone(data)
              : JSON.parse(JSON.stringify(data))
          );
          // Show message if no absence data found
          if (!data || data.length === 0) {
            showMessage({
              message: "No absence analysis data found.",
              type: "info",
              duration: 3000,
            });
          }
        } catch (error) {
          console.error("Error fetching absence analysis:", error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    } else {
      setAbsenceData([]);
    }
  }, [selectedFiscalYear, fiscalYearsLoaded, props.selectedEmployee]);

  useEffect(() => {
    if (absenceData && absenceData.length > 0) {
    }
  }, [absenceData]);

  const absenceAnimation = useRef(new Animated.Value(0)).current;

  const toggleAbsenceAnalysis = () => {
    setShowAbsenceAnalysis((prevState) => !prevState);
    Animated.timing(absenceAnimation, {
      toValue: showAbsenceAnalysis ? 0 : 1, // Toggle between 0 and 1
      duration: 300, // Duration of the animation
      useNativeDriver: true, // Use native driver for better performance
    }).start();
  };

  const checkIfUnsavedChangesInAnalysis = (hasChanges) => {
    setHasUnsavedChanges(hasChanges);
  };

  const handleEditPress = useCallback((index, item, key, value) => {
    if (item[key] === value) return;
    rowEdit(
      index,
      item,
      key,
      value,
      changes,
      setChanges,
      absenceData,
      setAbsenceData
    );
    checkIfUnsavedChangesInAnalysis(true);
  }, []);

  // const handleDeleteAbsence = (item, index, attribute) => {
  //   rowDelete(
  //     item,
  //     index,
  //     "analysisID",
  //     changes,
  //     setChanges,
  //     absenceData,
  //     setAbsenceData,
  //     setDeletedItems,
  //     ref
  //   );
  //   if (!insertData.includes(item.analysisID)) {
  //     checkIfUnsavedChangesInAnalysis(true);
  //   }
  // };

  const handleCancelBalanceChanges = () => {
    handleCancelChanges(
      setLeaveAnalysisData,
      originalData,
      () => { }, // no deletedItems needed
      setChanges,
      setDuplicates
    );
    pendingBalanceChanges.current = {};
    setBalancesResetVersion(v => v + 1);
    showMessage({ message: "Changes reverted.", type: "info", duration: 2000 });
  };

  //#region NewAnalysis
  const handleCreateAnalysisForYear = async (targetYear) => {
    if (!user || !props.selectedEmployee || !props.selectedCompany) {
      showMessage({
        message: t("common:missingUser"),
        type: "danger",
        duration: 3000,
      });
      return false;
    }

    const yearStr = String(targetYear ?? "").trim();
    const yearNum = parseInt(yearStr, 10);

    const existingYear = fiscalYears?.some(
      (y) => String(y?.value) === String(yearNum)
    );

    if (existingYear) {
      showMessage({
        message: `Leave analysis for year ${yearNum} already exists.`,
        type: "info",
        duration: 3000,
      });
      return false;
    }

    setIsLoading(true);

    try {
      const response = await createLeaveAnalysis(
        props.selectedEmployee.employeeID,
        props.selectedCompany.code,
        null,
        null,
        yearNum
      );

      if (response && response.status === 200) {
        const targetYearStr = String(yearNum);

        const yrs = await GetFiscalYears(props.selectedEmployee.employeeID);
        setFiscalYears(yrs ?? []);

        const leaveData = await leaveAnalysisGetById(
          props.selectedEmployee.employeeID,
          targetYearStr
        );

        const newLeaveData = leaveData || [];
        setLeaveAnalysisData(newLeaveData);
        setOriginalData(
          typeof structuredClone === "function"
            ? structuredClone(newLeaveData)
            : JSON.parse(JSON.stringify(newLeaveData))
        );

        const { startDate, endDate } = getFiscalYearDateRange(targetYearStr);
        const absData = await GetAbsenceAnalysis(
          props.selectedEmployee.employeeID,
          startDate,
          endDate
        );

        setAbsenceData(absData ?? []);
        setBalancesVersion((v) => v + 1);
        setSelectedFiscalYear(targetYearStr);

        showMessage({
          message: t("common:successLeaveaAnalysis"),
          type: "success",
          duration: 3000,
        });

        return true;
      } else {
        showMessage({
          message: t("common:failedTo"),
          type: "danger",
          duration: 3000,
        });
        return false;
      }
    } catch (error) {
      console.error("Error creating leave analysis:", error);
      showMessage({
        message: t("common:anErrorOccurred"),
        type: "danger",
        duration: 3000,
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isValidFiscalYear = (value) => {
    if (!/^\d{4}$/.test(String(value).trim())) return false;

    const year = parseInt(String(value).trim(), 10);
    const currentYear = new Date().getFullYear();

    return year >= currentYear - 10 && year <= currentYear + 10;
  };

  const getFiscalYearRangeMessage = () => {
    const currentYear = new Date().getFullYear();
    return `Accepted range: ${currentYear - 10} to ${currentYear + 10}`;
  };

  const handleCreateNextAnalysis = async () => {
    const currentYear = new Date().getFullYear();
    const nextFiscalYear = noFiscalYearsFound
      ? currentYear
      : (parseInt(selectedFiscalYear, 10) || currentYear) + 1;

    await handleCreateAnalysisForYear(nextFiscalYear);
  };

  const handleCreateCustomYear = async () => {
    if (!isValidFiscalYear(customYear)) {
      setCustomYearError(getFiscalYearRangeMessage());
      return;
    }

    setCustomYearError("");

    const success = await handleCreateAnalysisForYear(customYear);

    if (success) {
      setShowCreateYearModal(false);
      setCustomYear("");
      setCustomYearError("");
    }
  };

  // const handleAddNewAnalysis = () => {
  //   rowAdd(
  //     absenceData,
  //     setAbsenceData,
  //     ["analysisID"],
  //     [],
  //     ref,
  //     t,
  //     [],
  //     [],
  //     undefined,
  //     undefined,
  //     insertData,
  //     setInsertData,
  //     "analysisID"
  //   );
  // };

  const onTypeChange = (idx, itm, key, value) => {
    if (key !== "aCode") return; // Ensure only acode is updated here
    const newData = [...absenceData];
    if (newData[idx]) {
      newData[idx][key] = value;
      setAbsenceData(newData);
      setHasUnsavedChanges(true);
    }
  };

  const handleBalanceChange = (item, allowance, broughtForward, paid) => {
    pendingBalanceChanges.current[item.acode] = { item, allowance, broughtForward, paid };
  };

  const handleSaveAllBalances = async () => {
    if (Object.keys(pendingBalanceChanges.current).length === 0) {
      showMessage({ message: "No changes to save.", type: "info", duration: 3000 });
      return;
    }
    setIsLoading(true);
    try {
      for (const key of Object.keys(pendingBalanceChanges.current)) {
        const { item, allowance, broughtForward, paid } = pendingBalanceChanges.current[key];

        const taken = item.taken ?? 0;
        const broughtForwardExpired = item.broughtforwardExpired ?? 0;
        const remaining = allowance + broughtForward - taken - paid - broughtForwardExpired;

        /* Changes yearllyaAllowance for now to allowance since there are 2 columns in the table and unsure why*/
        //  difference = new allowance minus original yearlyAllowance
        // const difference = allowance - (item.yearlyAllowance ?? 0);
        const fiscalYearInt = parseInt(selectedFiscalYear, 10);
        const { startDate } = getFiscalYearDateRange(fiscalYearInt);
        const [y, m, d] = startDate.split("-").map(Number);
        const dateInFiscalYear = new Date(y, m - 1, d);

        await reconcileLeaveBalance(
          props.selectedEmployee.employeeID,
          item.acode,
          dateInFiscalYear,
          props.selectedCompany.code,
          broughtForward,
          paid,
          remaining,
          allowance,
          fiscalYearInt
        );
      }

      // Fetch all fresh data BEFORE touching any state
      const leaveData = await leaveAnalysisGetById(
        props.selectedEmployee.employeeID,
        selectedFiscalYear
      );
      const { startDate, endDate } = getFiscalYearDateRange(selectedFiscalYear);
      const absenceDataUpdated = await GetAbsenceAnalysis(
        props.selectedEmployee.employeeID,
        startDate,
        endDate
      );

      //updating all state at once 
      pendingBalanceChanges.current = {};
      setLeaveAnalysisData(leaveData || []);
      setAbsenceData(absenceDataUpdated || []);
      setOriginalData(
        typeof structuredClone === "function"
          ? structuredClone(absenceDataUpdated || [])
          : JSON.parse(JSON.stringify(absenceDataUpdated || []))
      );
      // forcing PersonalLeaveBalancesScreen remount WITH new data
      setBalancesVersion(v => v + 1);
      dispatch({ type: 'UPDATE_LEAVE_ANALYSIS', payload: leaveData || [] });

      showMessage({ message: "Balances saved successfully.", type: "success", duration: 3000 });
    } catch (error) {
      console.error("Error saving balances:", error);
      showMessage({ message: "Error saving balances.", type: "danger", duration: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  // const handleSaveAnalysis = async () => {
  //   try {
  //      // Validation: Ensure all absence records have a leave/absence type selected
  //   if (absenceData.some((analysis) => !analysis.aCode)) {
  //     showMessage({
  //       message: t(
  //         "Validation Error",
  //         "Please select an absence type for all records."
  //       ),
  //       type: "info",
  //       duration: 3000,
  //     });
  //     return;
  //   }
  //     // Validation: Ensure necessary fields are filled
  //     if (!absenceData || absenceData.some((analysis) => !analysis.Doa)) {
  //       showMessage({
  //         message: t("Validation Error", "Date of absence must be selected."),
  //         type: "info",
  //         duration: 3000,
  //       });
  //       return;
  //     }
  //     if (absenceData.some((analysis) => !analysis.EndDate)) {
  //       showMessage({
  //         message: t("Validation Error", "End date must be selected."),
  //         type: "info",
  //         duration: 3000,
  //       });
  //       return;
  //     }

  //     let savePromises = [];
  //     let updateSucc = true;
  //     let insertSucc = true;

  //     // Save new/updated absence analysis
  //     savePromises.push(
  //       SaveAbsenceAnalysis(
  //         absenceData,
  //         props?.selectedEmployee?.employeeID
  //       ).catch((error) => {
  //         console.error("Save error", error);
  //         updateSucc = false;
  //       })
  //     );
  //     // Delete rows marked for deletion
  //     for (const item of deletedItems) {
  //       savePromises.push(
  //         DeleteAbsenceAnalysis(`'${item.analysisID}'`).catch((error) => {
  //           insertSucc = false;
  //         })
  //       );
  //     }
  // for (const item of deletedItems) {
  //     if (item && item.SernoUI) { // Ensure item is defined and has SernoUI
  //         savePromises.push(
  //             DeleteAbsenceAnalysis(item.SernoUI).catch((error) => {
  //                 console.error('Delete error', error);
  //                 insertSucc = false;
  //             })
  //           )
  //                 console.error("absenceData is not an array or is undefined");
  //           }}

  // Wait for all promises to resolve
  //     await Promise.all(savePromises);

  //     // Handle results
  //     if (!updateSucc || !insertSucc) {
  //       showMessage({
  //         message: t(
  //           "Error",
  //           "An error occurred while saving the absence analysis."
  //         ),
  //         type: "info",
  //         duration: 3000,
  //       });
  //       return;
  //     }

  //     // Fetch updated list from backend
  //     const data = await GetAbsenceAnalysis(
  //       props?.selectedEmployee?.employeeID
  //     );

  //     // Update UI
  //     setAbsenceData(data);

  //     // Deep clone to avoid reference issues
  //     setOriginalData(
  //       typeof structuredClone === "function"
  //         ? structuredClone(data)
  //         : JSON.parse(JSON.stringify(data))
  //     );

  //     // Reset all change trackers
  //     setDeletedItems([]);
  //     setInsertData([]);
  //     setChanges({});
  //     setHasUnsavedChanges(false);

  //     // Reset any date flags or temporary states
  //     setResetDates(true);

  //     // Success message
  //     showMessage({
  //       message: t("Success", "Absence analysis saved successfully."),
  //       type: "info",
  //       duration: 3000,
  //     });
  //     removeFromChangesAfterSave();
  //   } catch (error) {
  //     console.error("Error during save:", error);
  //     // On error during save
  //     showMessage({
  //       message: t(
  //         "Error",
  //         "An error occurred while saving the absence analysis."
  //       ),
  //       type: "info",
  //       duration: 3000,
  //     });
  //   }
  // };

  // const removeFromChangesAfterSave = () => {
  //   setChanges([]);
  //   setDeletedItems([]);
  // };

  const handlePress = async (item, index) => { };

  const renderAbseceAnalysis = useCallback(
    ({ item, index }) => {
      return (
        <Card style={[common.cardstyle]}>
          <Card.Content>
            <LeaveAnalysisView
              item={item}
              index={index}
              props={props}
              styles={styles}
              handleEditPress={handleEditPress}
              duplicates={duplicates}
              // handleDeleteAbsence={handleDeleteAbsence}
              handlePress={handlePress}
              resetDates={resetDates}
              leaveTypesOptions={leaveTypesOptions}
              selectedCompany={selectedCompany}
              onTypeChange={onTypeChange}
            />
          </Card.Content>
        </Card>
      );
    },
    [
      resetDates,
      handleEditPress,
      // handleDeleteAbsence,
      handlePress,
      styles,
      leaveTypesOptions,
      absenceData,
    ]
  );

  if (isLoading || leaveTypesOptions.length === 0) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator animating={true} color={colors.blue} />
      </View>
    );
  }

  return (
    <View style={styles.mainView}>
      <ScrollView contentContainerStyle={styles.scrollViewContent}>
        <View
          style={[
            styles.fisy,
            { flexDirection: "row", alignItems: "center", gap: 10 },
          ]}
        >
          <CustomLabel value={t("common:leaveBalance")} type="SUBTITLE" />
          <CustomDropDown
            defValue={selectedFiscalYear}
            options={fiscalYears}
            setValue={onFiscalYearChange}
            type="NEW_COMPANY"
          />
          <CustomButton
            text="Create Next"
            onPress={handleCreateNextAnalysis}
            type="BUTTON_SAVE"
            isGradient={true}
            textTop="0"
            sz={15}
          />

          <CustomButton
            text={t("common:new")}
            onPress={() => {
              const defaultYear =
                noFiscalYearsFound
                  ? String(new Date().getFullYear())
                  : String((parseInt(selectedFiscalYear, 10) || new Date().getFullYear()) + 1);

              setCustomYear(defaultYear);
              setShowCreateYearModal(true);
            }}
            type="BUTTON_SAVE"
            isGradient={true}
            textTop="0"
            sz={15}
          />
          <CustomButton
            text="Save Balances"
            onPress={handleSaveAllBalances}
            type="BUTTON_SAVE"
            isGradient={true}
            textTop="0"
            sz={15}
          />
          <CustomButton
            text={t("common:cancel")}
            onPress={handleCancelBalanceChanges}
            type='BUTTON_CANCEL'
            textTop="0"
            sz={15}
          />
        </View>
        {noFiscalYearsFound && (
          <CustomLabel
            value={t("common:NOFY")}
            type="ASSM"
            style={{ marginHorizontal: 10, marginTop: 5 }}
          />
        )}
        <ScrollView
          horizontal={true}
          showsHorizontalScrollIndicator={true}
          contentContainerStyle={styles.balances}
        >
          {leaveAnalysisData && leaveAnalysisData.length > 0 ? (
            <PersonalLeaveBalancesScreen
              // key={leaveAnalysisData.length}
              key={`${selectedFiscalYear}-${balancesVersion}`}
              leaveAnalysis={leaveAnalysisData}
              isHRAdminEmplLeaveAnalysisScreen={true}
              ShowDetails={false}
              onBalanceChange={handleBalanceChange}
              resetVersion={balancesResetVersion}
            />
          ) : isLoading ? (
            <ActivityIndicator animating={true} color={colors.blue} />
          ) : (
            <CustomLabel value={t("common:noLeaveAnalysisData")} type="ASSM" />
          )}
        </ScrollView>
        <View style={styles.buttonContainer}>
          <View style={styles.line}>
            <CustomButton
              text={
                showAbsenceAnalysis
                  ? "Hide Absence Analysis"
                  : "Show Absence Analysis"
              }
              onPress={toggleAbsenceAnalysis}
              type="BUTTON_SAVE"
              isGradient={true}
              textTop="0"
              sz={15}
            />
          </View>
          <Ionicons
            name={
              showAbsenceAnalysis
                ? "chevron-down-outline"
                : "chevron-forward-outline"
            }
            style={styles.chevronIcon}
            onPress={toggleAbsenceAnalysis}
          />
        </View>
        {/* Animated View for Absence Analysis */}
        <Animated.View
          style={[
            styles.absenceAnalysisContainer,
            {
              opacity: absenceAnimation, // Animate opacity
              transform: [
                {
                  scale: absenceAnimation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.8, 1],
                  }),
                },
              ],
            },
          ]}
        >
          {showAbsenceAnalysis && (
            <>
              <FlatList
                data={absenceData}
                renderItem={renderAbseceAnalysis}
                keyExtractor={(item, index) => `${item.analysisID}-${index}`}
                refreshing={isLoading}
                ref={ref}
                ListHeaderComponent={
                  <View style={common.listTitle}>
                    <View style={styles.row}>
                      <CustomLabel value={t("common:startDate")} type="SUBTITLE" />
                      <CustomLabel value={t("common:endDate")} type="SUBTITLE" />
                      <CustomLabel value={t("common:absenceType")} type="SUBTITLE" />
                      <CustomLabel value={t("common:Dur")} type="SUBTITLE" />
                      <View style={styles.icons} />
                    </View>
                  </View>
                }
                ListEmptyComponent={
                  isLoading ? (
                    <ActivityIndicator animating={true} color={colors.blue} />
                  ) : (
                    <CustomLabel
                      value={t("common:noAbsenceData")}
                      type="ASSM"
                    />
                  )
                }
              />
              {/* <View style={[styles.buttons, styles.functionalityButtons]}> */}
              {/* <CustomButton
                  text={t("common:new")}
                  onPress={handleAddNewAnalysis}
                  type="BUTTON_ADD"
                  textTop="0"
                  sz={15}
                /> */}
              {/* <CustomButton
                  text={t("common:saveFunction")}
                  onPress={handleSaveAnalysis}
                  type="COMPANY_BRANCHES_SAVE"
                  textTop="0"
                  sz={15}
                /> */}
              {/* <CustomButton
                  text={t("common:cancel")}
                  onPress={() =>
                    handleCancelChanges(
                      setAbsenceData,
                      originalData ?? [],
                      setDeletedItems,
                      setChanges,
                      setDuplicates
                    )
                  }
                  type="COMPANY_BRANCHES_CANCEL"
                  textTop="0"
                  sz={15}
                /> */}
              {/* </View> */}
            </>
          )}
        </Animated.View>
      </ScrollView>

      <Modal
        visible={showCreateYearModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowCreateYearModal(false);
          setCustomYear("");
          setCustomYearError("");
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <CustomLabel value={t("common:createLA")} type="SUBTITLE" />
            <CustomLabel
              value={t("common:enterYear")}
              type="ASSM"
              style={{ marginTop: 8, marginBottom: 12 }}
            />

            <CustomInput
              value={customYear}
              setValue={(val) => {
                const cleaned = val.replace(/[^0-9]/g, "").slice(0, 4);
                setCustomYear(cleaned);

                if (customYearError) {
                  setCustomYearError("");
                }
              }}
              maxLen={4}
              type="MAININPUT"
              keyboardType="numeric"
            />

            {customYearError ? (
              <CustomLabel
                value={customYearError}
                type="ASSM"
                style={{ color: "red", marginTop: 8 }}
              />
            ) : null}

            <View style={styles.modalButtons}>
              <CustomButton
                text={t("common:cancel")}
                onPress={() => {
                  setShowCreateYearModal(false);
                  setCustomYear("");
                  setCustomYearError("");
                }}
                type="BUTTON_CANCEL"
                textTop="0"
                sz={15}
              />
              <CustomButton
                text="Create"
                onPress={handleCreateCustomYear}
                type="BUTTON_SAVE"
                isGradient={true}
                textTop="0"
                sz={15}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const makeStyles = (colors, width, props) =>
  StyleSheet.create({
    loader: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    mainView: {
      flex: 1,
      paddingHorizontal: 5,
    },
    container: {
      flex: 1,
    },
    item: {
      flex: 1,
      justifyContent: "center",
      marginRight: 6,
    },
    row: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
    },
    row2: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      marginRight: 22,
    },
    title: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: 3,
      marginLeft: 10,
    },
    label: {
      fontWeight: "500",
      marginBottom: 4,
    },
    input: {
      borderWidth: 1,
      borderColor: "#0000",
      borderRadius: 4,
      padding: 8,
      backgroundColor: "#fafafa",
    },
    tableRow: {
      flexDirection: "row",
      alignItems: "center",
      borderColor: "#eee",
      marginVertical: 5,
    },
    headerRow: {
      marginTop: 3,
    },
    cell: {
      flex: 1,
      padding: 8,
    },
    icons: {
      fontSize: 24,
      marginHorizontal: 10,
      marginTop: 6,
    },
    chevronIcon: {
      fontSize: 24,
      marginLeft: 8,
      alignSelf: "center",
    },
    fisy: {
      justifyContent: "space-between",
      flexDirection: "row",
      alignItems: "center",
      alignSelf: "flex-start",
      marginVertical: 10,
      marginHorizontal: 10,
      width: Platform.OS === "web" && width > 1300 ? width * 0.4 : width * 0.6,
    },
    buttons: {
      flex: 1,
      flexDirection: "row",
      justifyContent: "flex-end",
      alignSelf: "flex-end",
      marginRight: 10,
    },
    functionalityButtons: {
      justifyContent: "flex-end",
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
    },
    line: {
      borderBottomColor: colors.buttons_primary,
      borderBottomWidth: StyleSheet.hairlineWidth,
      marginTop: "1%",
      marginBottom: "1%",
      marginHorizontal: 20,
    },
    balances: {
      flexDirection: "row",
    },
    absenceAnalysisContainer: {
      overflow: "hidden",
    },
    buttonContainer: {
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.35)",
      justifyContent: "center",
      alignItems: "center",
      paddingHorizontal: 20,
    },
    modalContainer: {
      width: Platform.OS === "web" ? 420 : "90%",
      backgroundColor: colors.card,
      borderRadius: 12,
      padding: 20,
      elevation: 5,
    },
    modalButtons: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 16,
    },
  });

export default HRAdminEmplLeaveAnalysisScreen;
